import { useEffect } from 'react';
import { WORLDS, WorldKey, WorldTheme, worldCssVars } from '../config/theme';
import { useReducedMotion } from './useReducedMotion';

/**
 * Install, once, the @property registrations that make the accent CSS vars
 * animatable, plus a transition. Without @property, custom properties jump
 * instantly; registering them as <color> lets the browser tween them.
 */
let injected = false;
function ensureGlobalThemeStyle(reduced: boolean) {
  if (injected || typeof document === 'undefined') return;
  injected = true;
  const dur = reduced ? '0s' : '0.5s';
  const css = `
    @property --ep-accent { syntax: '<color>'; inherits: true; initial-value: #4f46e5; }
    @property --ep-accent-dark { syntax: '<color>'; inherits: true; initial-value: #4338ca; }
    @property --ep-accent-soft { syntax: '<color>'; inherits: true; initial-value: #eef2ff; }
    :root {
      transition: --ep-accent ${dur} ease, --ep-accent-dark ${dur} ease, --ep-accent-soft ${dur} ease;
    }
  `;
  const el = document.createElement('style');
  el.setAttribute('data-ep-theme', 'true');
  el.textContent = css;
  document.head.appendChild(el);
}

/**
 * Activates a "world" theme: writes the world's accent tokens as CSS variables
 * on <html> so the whole app re-tints, and animates the change (the accent
 * eases from one world's color to the next) unless reduced motion is set.
 *
 * Call once near the top of each shell (operator / organization / attendee).
 * Returns the resolved token object for components that prefer inline styles.
 */
export function useWorldTheme(world: WorldKey): WorldTheme {
  const theme = WORLDS[world];
  const reduced = useReducedMotion();

  useEffect(() => {
    ensureGlobalThemeStyle(reduced);
    const root = document.documentElement;
    const vars = worldCssVars(theme);
    for (const [k, v] of Object.entries(vars)) {
      root.style.setProperty(k, v);
    }
    root.setAttribute('data-ep-world', theme.key);
    // Leave vars in place on unmount so there's no accent flash between the
    // route transition; the next shell's hook overwrites them immediately.
  }, [theme, reduced]);

  return theme;
}
